import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  TrendingUp,
  QrCode,
  LayoutGrid,
  Boxes,
  Headphones,
  ShieldCheck,
  Network,
  KeyRound,
  Workflow,
  Cable,
  Handshake,
  HardHat,
  Home as HomeIcon,
  IndianRupee,
} from "lucide-react";
import { SectionLabel, openLeadModal } from "./_shared";

const DEALER_POINTS = [
  { icon: TrendingUp, t: "Territory exclusivity", d: "Defined territory rights, fair lead routing, and pricing that protects your margins." },
  { icon: QrCode, t: "QR-bound dispatch", d: "Every barrier you receive ships with a verifiable test record — your customers trust what you sell." },
  { icon: LayoutGrid, t: "Dealer dashboard", d: "Track your installed base, warranty status, and service tickets on a single live screen." },
  { icon: Boxes, t: "Marketing kit included", d: "Spec sheets, demo videos, brochures and on-site posters — co-branded, ready for the field." },
  { icon: Headphones, t: "Engineering hotline", d: "Direct line to BBC engineers for site queries — no call-centre maze, no escalation tickets." },
  { icon: ShieldCheck, t: "2-year warranty backing", d: "Replacement parts and RMA flow handled centrally — you're never holding the bag." },
];

const ENTERPRISE_POINTS = [
  { icon: Network, t: "Fleet management at scale", d: "Map every barrier across every site on one screen — uptime, faults, service history." },
  { icon: KeyRound, t: "Remote payment-lock", d: "Tie barrier access to your accounts ledger. Disable any gate remotely until the payment hits — restored the moment finance clears." },
  { icon: ShieldCheck, t: "SLA-backed support", d: "Response and resolution SLAs aligned with your operational tier — 24×7 available." },
  { icon: Workflow, t: "Multi-site rollouts", d: "Phased deployment plans, on-site commissioning teams, and standardised site SOPs." },
  { icon: Cable, t: "API & integration", d: "Plug fleet telemetry and remote-control hooks into your CRM, CAFM, BMS or VMS — RS485 / Ethernet / cloud." },
  { icon: Handshake, t: "Dedicated account manager", d: "One named point of contact across your whole portfolio. No round-robin." },
];

const PATHWAY_USE_CASES = [
  {
    slug: "builder-sites",
    icon: HardHat,
    eyebrow: "Builder Sites",
    title: "Force the payment schedule.",
    body: "Buyer hasn't paid the next instalment? Project-gate barriers are remotely disabled from BBC Trust until the payment clears — restored the moment finance signs off.",
  },
  {
    slug: "rental-properties",
    icon: HomeIcon,
    eyebrow: "Rental Properties",
    title: "Rent overdue? Gate stops opening.",
    body: "PG owners, co-living operators and rental builders can tie barrier access to rent status — a 30-day overdue tenant is locked out automatically, no confrontation at the gate.",
  },
  {
    slug: "pay-per-use",
    icon: IndianRupee,
    eyebrow: "Toll & Pay-Per-Use",
    title: "Operator-controlled gates.",
    body: "Run pay-per-entry parking, event venues and commercial yards remotely. Open, close, lock or schedule any barrier — no on-site guard needed for routine control.",
  },
];

const PathwayCard = ({ idLabel, badge, accent, title, lede, points, ctaLabel, leadType, testIdPrefix }) => (
  <article
    data-testid={`pathway-${testIdPrefix}`}
    className="relative border bg-[#16191C] overflow-hidden flex flex-col"
    style={{ borderColor: "rgba(255,255,255,0.10)" }}
  >
    <div
      className="absolute top-0 left-0 right-0 h-px"
      style={{ background: `linear-gradient(90deg, transparent, ${accent}, transparent)`, opacity: 0.7 }}
    />
    <header className="px-7 md:px-8 pt-7 md:pt-8 pb-6 border-b border-white/5">
      <div className="flex items-center justify-between">
        <span className="font-mono text-[10px] uppercase tracking-[0.25em]" style={{ color: accent }}>{idLabel}</span>
        <span className="inline-flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.22em]" style={{ color: accent }}>
          <span className="w-1.5 h-1.5 rounded-full bbc-pulse" style={{ background: accent }} />
          {badge}
        </span>
      </div>
      <h3 className="font-display mt-3 text-white text-3xl md:text-4xl font-black tracking-[-0.02em] leading-[1.05]">{title}</h3>
      <p className="mt-3 text-[#A1A1AA] text-sm md:text-base font-body leading-relaxed">{lede}</p>
    </header>

    <ul className="grid sm:grid-cols-2 gap-px bg-white/5 flex-1">
      {points.map((p, i) => {
        const Icon = p.icon;
        return (
          <li key={i} data-testid={`${testIdPrefix}-point-${i}`} className="bg-[#16191C] px-6 py-5">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 border flex items-center justify-center shrink-0" style={{ borderColor: `${accent}40`, background: `${accent}10` }}>
                <Icon size={15} style={{ color: accent }} />
              </div>
              <div className="min-w-0">
                <h4 className="font-display text-white text-[14px] font-bold tracking-tight leading-tight">{p.t}</h4>
                <p className="mt-1.5 text-[#A1A1AA] text-[12px] font-body leading-relaxed">{p.d}</p>
              </div>
            </div>
          </li>
        );
      })}
    </ul>

    <footer className="px-7 md:px-8 py-6 border-t border-white/5 flex items-center justify-between flex-wrap gap-3 bg-[#13161A]">
      <div>
        <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#71717A]">One business day response</div>
        <div className="font-display mt-1 text-white text-sm font-bold tracking-tight">Ready when you are.</div>
      </div>
      <button
        type="button"
        onClick={() => openLeadModal(leadType, `partners-${testIdPrefix}`)}
        data-testid={`pathway-${testIdPrefix}-cta`}
        className="group cursor-pointer inline-flex items-center gap-3 font-semibold px-6 py-3.5 text-xs uppercase tracking-wider transition-all"
        style={{ background: accent, color: "#0F1113", boxShadow: `0 0 24px ${accent}40` }}
      >
        {ctaLabel}
        <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
      </button>
    </footer>
  </article>
);

export const SegmentedPathways = () => (
  <section
    id="partners"
    data-testid="platform-partners"
    aria-labelledby="partners-heading"
    className="relative py-24 md:py-32 border-t border-white/5 bg-[#13161A] overflow-hidden"
  >
    <div className="absolute inset-0 bbc-grid-bg opacity-[0.06] pointer-events-none" />
    <div
      className="absolute -top-40 right-1/4 w-[600px] h-[600px] rounded-full blur-3xl pointer-events-none"
      style={{ background: "radial-gradient(circle, rgba(0,255,133,0.08), transparent 60%)" }}
    />

    <div className="relative max-w-7xl mx-auto px-6 md:px-12">
      <SectionLabel id={8} label="Built for the Two Sides of the Market" />
      <div className="grid lg:grid-cols-12 gap-8 mt-4 items-end">
        <h2 id="partners-heading" className="lg:col-span-7 font-display text-white text-4xl md:text-5xl lg:text-6xl font-black tracking-[-0.02em] leading-[1.02]">
          One platform.
          <br />
          <span className="text-[#00FF85]">Two pathways.</span>
        </h2>
        <p className="lg:col-span-5 text-[#A1A1AA] text-base md:text-lg font-body lg:pb-2">
          Whether you sell BBC barriers or you operate hundreds of them across a portfolio — we've built a track for each side.
        </p>
      </div>

      <div className="mt-14 grid lg:grid-cols-2 gap-3 lg:gap-4 items-stretch">
        <PathwayCard
          idLabel="/// — TRACK.01 · For Channel Partners"
          badge="Dealers"
          accent="#00FF85"
          title="Sell smarter barriers. Grow a defensible territory."
          lede="Partner with the first traceable boom-barrier brand in India. Higher margins, fair lead routing, and a marketing kit your team can actually use."
          points={DEALER_POINTS}
          ctaLabel="Become a Dealer"
          leadType="dealer"
          testIdPrefix="dealer"
        />
        <PathwayCard
          idLabel="/// — TRACK.02 · For Enterprises & Operators"
          badge="Property & Operations"
          accent="#B0FFD9"
          title="Run a fleet of barriers like infrastructure."
          lede="Multi-site rollouts, SLA-backed support and a fleet dashboard your facilities team can hand to the CIO. Built for serious portfolios."
          points={ENTERPRISE_POINTS}
          ctaLabel="Talk to an Enterprise Lead"
          leadType="sales"
          testIdPrefix="enterprise"
        />
      </div>

      <div className="mt-16" data-testid="pathways-use-cases">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#00FF85]">
              /// — Built for these critical verticals
            </div>
            <h3 className="font-display mt-2 text-white text-2xl md:text-3xl lg:text-4xl font-black tracking-[-0.02em] leading-[1.05]">
              Where remote control
              <br className="hidden sm:inline" />
              <span className="text-[#A1A1AA]"> becomes a payment lever.</span>
            </h3>
          </div>
          <p className="md:max-w-md text-[#A1A1AA] text-sm md:text-base font-body leading-relaxed">
            Every BBC barrier can be opened, closed, locked or disabled remotely from the BBC Trust app — a non-negotiable capability for the verticals below.
          </p>
        </div>

        <div className="mt-8 grid md:grid-cols-3 gap-3">
          {PATHWAY_USE_CASES.map((uc, i) => {
            const Icon = uc.icon;
            return (
              <Link
                key={i}
                to={`/use-cases/${uc.slug}`}
                data-testid={`pathways-usecase-${i}`}
                className="group relative border border-white/10 bg-[#16191C] p-6 md:p-7 hover:border-[#00FF85]/40 transition-all overflow-hidden block"
              >
                <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#00FF85]/40 to-transparent" />
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 border border-[#00FF85]/30 bg-[#00FF85]/[0.06] flex items-center justify-center">
                    <Icon size={16} className="text-[#00FF85]" />
                  </div>
                  <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#00FF85]">
                    {uc.eyebrow}
                  </span>
                </div>
                <h4 className="font-display mt-5 text-white text-lg md:text-xl font-black tracking-tight leading-snug">
                  {uc.title}
                </h4>
                <p className="mt-3 text-[#A1A1AA] text-sm font-body leading-relaxed">
                  {uc.body}
                </p>
                <div className="mt-5 inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.22em] text-[#00FF85] group-hover:gap-2.5 transition-all">
                  Read full use case
                  <ArrowRight size={11} />
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  </section>
);
